import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Landmark, Satellite, ScanSearch, Siren, ClipboardList,
  FileText, ShieldCheck, Users, Building2, Settings, Activity, Layers, Bell,
  ClipboardCheck, Presentation,
} from 'lucide-react';
import { Wordmark } from '@/components/brand/Logo';
import { useAuth, canManageAssets, canManageOrgs, isFieldUser } from '@/lib/auth';
import { Separator } from '@/components/ui/separator';

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
        isActive
          ? 'bg-primary/10 text-primary font-medium border border-primary/20'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
      }`
    }
  >
    <Icon size={18} strokeWidth={1.75} />
    <span className="truncate">{label}</span>
  </NavLink>
);

const SectionTitle = ({ children }) => (
  <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
    {children}
  </div>
);

export function Sidebar() {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.role;
  const showOrgs = canManageOrgs(role);
  const showAdmin = canManageAssets(role);
  const field = isFieldUser(role);

  return (
    <aside
      className="w-[260px] shrink-0 border-r border-border bg-card flex flex-col h-screen sticky top-0 z-40"
      data-testid="app-sidebar"
    >
      <div className="px-4 py-4 border-b border-border">
        <NavLink to="/app/dashboard" data-testid="sidebar-brand">
          <Wordmark />
        </NavLink>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {!field && (
          <>
            <SectionTitle>Operations</SectionTitle>
            <NavItem to="/app/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
            <NavItem to="/app/assets" icon={Landmark} label="Assets" testId="nav-assets" />
            <NavItem to="/app/alerts" icon={Siren} label="Alerts" testId="nav-alerts" />
            <NavItem to="/app/incidents" icon={ClipboardList} label="Incidents" testId="nav-incidents" />
            <SectionTitle>Intelligence</SectionTitle>
            <NavItem to="/app/observations" icon={Satellite} label="Observations" testId="nav-observations" />
            <NavItem to="/app/detections" icon={ScanSearch} label="AI observations" testId="nav-detections" />
            <NavItem to="/app/reports" icon={FileText} label="Reports" testId="nav-reports" />
            <NavItem to="/app/audit" icon={ShieldCheck} label="Audit trail" testId="nav-audit" />
            <SectionTitle>Presentation</SectionTitle>
            <NavItem to="/app/presentation" icon={Presentation} label="Presentation Mode" testId="nav-presentation" />
          </>
        )}

        {field && (
          <>
            <SectionTitle>Field Operations</SectionTitle>
            <NavItem to="/app/incidents" icon={ClipboardCheck} label="My Incidents" testId="nav-my-incidents" />
          </>
        )}

        {showAdmin && (
          <>
            <SectionTitle>Administration</SectionTitle>
            {showOrgs && (
              <NavItem to="/app/admin/organizations" icon={Building2} label="Organisations" testId="nav-orgs" />
            )}
            <NavItem to="/app/admin/users" icon={Users} label="Users" testId="nav-users" />
            <NavItem to="/app/admin/config" icon={Settings} label="Configuration" testId="nav-config" />
            {showOrgs && (
              <NavItem to="/app/admin/health" icon={Activity} label="System Health" testId="nav-health" />
            )}
          </>
        )}
      </nav>

      <div className="px-4 py-3 border-t border-border">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Signed in as</div>
        <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.full_name}</div>
        <div className="text-xs text-muted-foreground truncate">{user.email}</div>
      </div>
    </aside>
  );
}
